<script setup>
import isEqual from 'lodash/isEqual'

const apiCompany = useApiCompany()
const paginator = usePaginator(getResults, 'companies')

const sort = ref('match:DESC')
const filters = ref([])
const search = ref('')
const disableTagAndGeoFilters = ref(true)
// Needed to reset the loader
const infiniteLoaderIdentifier = ref(Math.random(999999))

// Need to wrap this for better DX in template and dev tools
const meta = computed(() => paginator.meta.value)
const companies = computed(() => paginator.results.value)

const heroSubtitle = computed(() => {
  const total = meta.value?.pagination?.total || 0
  return total === 1 ? `${total} resultaat` : `${total} resultaten`
})

const searchfilter = computed(() => {
  if (search.value) {
    return {
      name: {
        $containsi: search.value,
      },
    }
  }
  return {}
})

const formattedFilters = computed(() => {
  if (filters.value.length === 0) return {}
  const groups = []
  for (const filter of filters.value) {
    const group = groups.find((g) => g.id === filter.parent.id)
    if (group) {
      group.filters.push(filter)
    } else {
      groups.push({
        id: filter.parent.id,
        filters: [filter],
      })
    }
  }

  return {
    $and: groups.map((group) => ({
      tags: {
        id: {
          $in: group.filters.map((f) => f.id),
        },
      },
    })),
  }
})
const query = computed(() => ({
  filters: {
    $and: [searchfilter.value, formattedFilters.value],
  },
  populate: ['logo', 'address', 'tags', 'tags.parent', 'tags.children'],
  sort: sort.value,
  useGeoFilter: !disableTagAndGeoFilters.value,
  useTagFilter: !disableTagAndGeoFilters.value,
}))
watchDebounced(query, resetPaginatorAndLoader, { debounce: 300 })

async function getResults(page, query) {
  return await apiCompany.getCompanies({
    ...query,
    pagination: {
      page,
      pageSize: 25,
    },
  })
}

function resetPaginatorAndLoader(newVal, oldVal) {
  if (isEqual(newVal, oldVal)) return
  paginator.reset(query.value)
  infiniteLoaderIdentifier.value = Math.random(999999)
}

await useAsyncData('paginatedCompanies', () => paginator.start(query.value))
</script>

<template>
  <div>
    <OverviewHero title="Bedrijven op het platform" :subtitle="heroSubtitle" />
    <Container>
      <div class="lg:flex">
        <CompaniesOverviewFilter
          v-model:filters="filters"
          v-model:search="search"
          v-model:disable-tag-and-geo-filters="disableTagAndGeoFilters"
        />
        <div class="w-full lg:w-2/3">
          <CompaniesOverviewToolbar v-model:sort="sort" />
          <div v-if="companies.length > 0">
            <CompaniesOverviewRow
              v-for="(company, index) in companies"
              :key="index"
              :company
            />
          </div>
          <ClientOnly>
            <InfiniteLoader
              :identifier="infiniteLoaderIdentifier"
              class="mt-8"
              @infinite="paginator.next"
            />
          </ClientOnly>
        </div>
      </div>
    </Container>
  </div>
</template>
